import axios from "axios";
import { trackPromise } from "react-promise-tracker";
import { clearCache, getCachedData } from "@core/utils/localStorageManager";

const LEGACY_DEV_API_URL = "http://ilaand.ir";

/** Strip trailing slashes so axios resolves `/api/1/...` paths consistently. */
const normalizeBaseUrl = (url) => {
  if (url == null || url === "") {
    return "";
  }
  return String(url).replace(/\/+$/, "");
};

/**
 * API origin. Set via REACT_APP_API_URL (.env.development / build args).
 * - development default: ilaand.ir (legacy) when env unset
 * - production default: same-origin (empty string)
 */
export const BASE_URL = normalizeBaseUrl(
  process.env.REACT_APP_API_URL ??
    (process.env.NODE_ENV === "development" ? LEGACY_DEV_API_URL : "")
);

export const METHOD = {
  GET: "get",
  HEAD: "HEAD",
  PUT: "PUT",
  DELETE: "DELETE",
  PATCH: "PATCH",
  POST: "POST",
};

const singleton = Symbol();
const singletonEnforcer = Symbol();

const defaultOptions = {
  baseURL: BASE_URL,
  method: METHOD.GET,
  headers: {
    "Content-Type": "application/json",
  },
};

class ApiService {
  constructor(enforcer) {
    if (enforcer !== singletonEnforcer) {
      throw new Error("Cannot construct singleton");
    }
    const defaultOptions = {
      baseURL: BASE_URL,
      method: METHOD.GET,
      headers: {
        "Content-Type": "application/json",
      },
    };

    this.session = axios.create(defaultOptions);

    this.session.interceptors.request.use((config) => {
      const token = getCachedData("access_token");
      
      if (token) {
        config.headers["Authorization"] = `${token}`;
      }
      if (config.data?.captcha) {
        config.headers["captcha-response"] = config.data?.captcha;
        delete config.data?.captcha;
      }
      // config.headers["X-Platform"] = config.headers["X-Platform"] ? config.headers["X-Platform"] : `Web`;
      config.params = Object.assign({}, config.params || {});

      return config;
    });

    this.session.interceptors.response.use(
      (response) => {
        return response.data;
      },
      (error) => {
        if (error.response && error.response.status === 401) {
          const url = String(error.config?.url || "");
          const isAuthRequest =
            url.includes("/authenticate") ||
            url.includes("/register") ||
            url.includes("/forgot/");

          if (!isAuthRequest) {
            const requestToken = error.config?.headers?.Authorization;
            const currentToken = getCachedData("access_token");
            // Ignore stale in-flight responses that used an older token.
            if (
              !requestToken ||
              !currentToken ||
              requestToken === currentToken
            ) {
              clearCache();
            }
          }
        }
        // handle the response error
        return Promise.reject(error?.response?.data);
      }
    );
  }

  static get instance() {
    if (!this[singleton]) {
      this[singleton] = new ApiService(singletonEnforcer);
    }

    return this[singleton];
  }

  get = (url, options = {}) =>
    trackPromise(this.session.get(url, { ...defaultOptions, ...options }));
  getWithoutLoading = (url, options = {}) =>
    this.session.get(url, { ...defaultOptions, ...options });
  post = (url, data, options = {}) =>
    trackPromise(
      this.session.post(url, data, { ...defaultOptions, ...options })
    );
  postWithoutLoading = (url, data, options = {}) =>
    this.session.post(url, data, { ...defaultOptions, ...options });
  put = (url, data, options = {}) =>
    trackPromise(
      this.session.put(url, data, { ...defaultOptions, ...options })
    );
  putWithoutLoading = (url, data, options = {}) =>
    this.session.put(url, data, { ...defaultOptions, ...options });
  patch = (url, data, options = {}) =>
    trackPromise(
      this.session.patch(url, data, { ...defaultOptions, ...options })
    );
  delete = (url, options = {}) =>
    trackPromise(this.session.delete(url, { ...defaultOptions, ...options }));
  remove = (...params) => this.session.delete(...params);
}

export default ApiService.instance;
